import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Download, PenLine, Trash2, Lock } from "lucide-react";
import { toast } from "sonner";
import { api, fmtErr, fmtDate, fmtDateTime } from "../lib/api";
import { useAuth } from "../lib/auth";
import { RagBadge } from "../components/Badges";
import { SignaturePad } from "../components/SignaturePad";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Tr, TalkPoints, LangSelect, ListenButton, downloadTalk, translated } from "../components/TalkLibrary";

const TalkRead = ({ s, lang }) => (
  <details open className="rounded border border-slate-800 p-2" data-testid="sign-talk-content">
    <summary className="cursor-pointer text-sm font-semibold">Read the talk{s.points[0].tr?.[lang] ? ` (English / ${lang})` : ""}</summary>
    <div className="my-2"><ListenButton talkId={s.talk_id} lang={lang} testid="sign-listen" /></div>
    <TalkPoints points={s.points} lang={lang} />
  </details>
);

const SignDialog = ({ s, meta, open, onClose, onSigned }) => {
  const blank = { name: "", user_id: null, language: (s.talk_id && translated(s.languages)[0]) || s.languages[0], understood: false, quiz_answers: s.quiz.map(() => null), signature: "" };
  const [f, setF] = useState(blank);
  const [key, setKey] = useState(0);
  const pick = (e) => {
    const u = meta.users.find((x) => x.name.toLowerCase() === e.target.value.toLowerCase());
    setF({ ...f, name: e.target.value, user_id: u?.id || null });
  };
  const save = async () => {
    try {
      const r = await api.post(`/training/${s.id}/attendees`, f);
      toast[r.data.passed === false ? "warning" : "success"](r.data.passed === false ? `${f.name} signed - quiz ${r.data.score}%, retraining needed` : `${f.name} signed`);
      setF(blank);
      setKey(key + 1);
      onSigned();
    } catch (e) { toast.error(fmtErr(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto border-slate-700 bg-ink-2 sm:max-w-lg" data-testid="sign-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl uppercase">Attendee sign-off</DialogTitle></DialogHeader>
        <input className="field" list="training-users" placeholder="Attendee name (pick a user or type any name)" value={f.name} onChange={pick} data-testid="sign-name" />
        <datalist id="training-users">{meta.users.map((u) => <option key={u.id} value={u.name} />)}</datalist>
        <label className="text-xs text-slate-400">Language the attendee understood the training in
          <select className="field mt-1" value={f.language} onChange={(e) => setF({ ...f, language: e.target.value })} data-testid="sign-language">{meta.languages.map((l) => <option key={l}>{l}</option>)}</select>
        </label>
        {s.points && <TalkRead s={s} lang={f.language} />}
        {s.quiz.map((q, i) => (
          <div key={i} className="space-y-1 rounded border border-slate-800 p-2" data-testid={`sign-quiz-${i}`}>
            <p className="text-sm font-semibold">{i + 1}. {q.q}</p>
            {q.tr?.[f.language] && <Tr lang={f.language} className="text-sm font-semibold">{q.tr[f.language].q}</Tr>}
            {q.options.map((o, k) => (
              <label key={k} className="flex flex-wrap items-center gap-2 text-sm"><input type="radio" name={`q${i}`} checked={f.quiz_answers[i] === k} onChange={() => setF({ ...f, quiz_answers: f.quiz_answers.map((x, j) => (j === i ? k : x)) })} data-testid={`sign-quiz-${i}-opt-${k}`} />{o}{q.tr?.[f.language] && <Tr lang={f.language} className="ml-auto">{q.tr[f.language].options[k]}</Tr>}</label>
            ))}
          </div>
        ))}
        <label className="flex items-start gap-2 rounded border border-amber-500/40 bg-amber-500/10 p-3 text-sm" data-testid="sign-understood-label">
          <input type="checkbox" className="mt-0.5" checked={f.understood} onChange={(e) => setF({ ...f, understood: e.target.checked })} data-testid="sign-understood" />
          <span>I confirm I was trained in a language I understand and I understood the content of this {s.type.toLowerCase()}.
            {s.confirm_tr?.[f.language] && f.language !== "English" && <Tr lang={f.language} className="mt-1">{s.confirm_tr[f.language]}</Tr>}</span>
        </label>
        <SignaturePad key={key} onChange={(v) => setF((p) => ({ ...p, signature: v }))} testid="sign-signature" />
        <div className="flex justify-end gap-2"><Button variant="ghost" onClick={onClose}>Done</Button><Button disabled={!f.name.trim() || !f.understood || !f.signature} className="bg-rose-600 hover:bg-rose-700" onClick={save} data-testid="sign-save">Save signature</Button></div>
      </DialogContent>
    </Dialog>
  );
};

const Info = ({ s, lang, setLang }) => (
  <div className="panel grid gap-x-6 gap-y-2 p-4 text-sm sm:grid-cols-2" data-testid="training-info">
    {[["Type", s.type], ["Date", fmtDate(s.date)], ["Trainer", s.trainer], ["SOP / document", s.sop_ref || "-"], ["Language(s)", s.languages.join(", ")], ["Translator", s.translator || "None"],
      ["Refresher", s.refresher_months ? `Every ${s.refresher_months} months` : "Not required"], ["Quiz", s.quiz.length ? `${s.quiz.length} question(s), pass ${s.pass_mark}%` : "None"]].map(([k, v]) => (
      <p key={k}><span className="eyebrow mr-2">{k}</span>{v}</p>
    ))}
    {s.description && !s.points && <p className="sm:col-span-2"><span className="eyebrow mr-2">Content</span>{s.description}</p>}
    {s.points && (
      <div className="sm:col-span-2">
        <div className="mb-1 flex flex-wrap items-center justify-between gap-2"><p className="eyebrow">Talk content (English / {lang})</p><div className="flex flex-wrap gap-2"><ListenButton talkId={s.talk_id} lang={lang} testid="training-listen" /><LangSelect langs={translated(Object.keys(s.title_tr || {}))} value={lang} onChange={setLang} testid="training-view-lang" /></div></div>
        {s.title_tr?.[lang] && <Tr lang={lang} className="mb-2 border-b border-slate-800 pb-2 text-lg">{s.title_tr[lang]}</Tr>}
        <TalkPoints points={s.points} lang={lang} />
      </div>
    )}
  </div>
);

export default function TrainingSession() {
  const { id } = useParams();
  const nav = useNavigate();
  const { can } = useAuth();
  const [s, setS] = useState(null);
  const [meta, setMeta] = useState(null);
  const [signing, setSigning] = useState(false);
  const [lang, setLang] = useState(null);
  const load = useCallback(() => api.get(`/training/${id}`).then((r) => { setS(r.data); setLang((l) => l || translated(Object.keys(r.data.title_tr || {}))[0]); }).catch((e) => toast.error(fmtErr(e))), [id]);
  useEffect(() => { load(); api.get("/training/meta").then((r) => setMeta(r.data)); }, [load]);
  if (!s) return null;
  const open = s.status === "open";
  const act = async (fn, msg) => { try { await fn(); toast.success(msg); load(); } catch (e) { toast.error(fmtErr(e)); } };
  const pdf = async () => {
    try {
      const r = await api.get(`/training/${id}/pdf`, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(r.data);
      a.download = `${s.ref}_training_register.pdf`;
      a.click();
    } catch (e) { toast.error("Could not create the PDF"); }
  };
  return (
    <div className="space-y-4" data-testid="training-session-page">
      <button onClick={() => nav("/training")} className="flex items-center gap-1 text-sm text-slate-400 hover:text-slate-100" data-testid="training-back"><ArrowLeft className="h-4 w-4" />Training</button>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div><p className="font-mono text-xs text-cyan-300">{s.ref}</p><h1 className="font-display text-3xl font-extrabold uppercase">{s.topic}</h1></div>
        <div className="flex flex-wrap gap-2">
          <RagBadge status={open ? "amber" : "green"} label={s.status} />
          <Button variant="outline" className="border-slate-700 bg-transparent" onClick={pdf} data-testid="training-pdf-btn"><Download className="mr-1 h-4 w-4" />Signed register PDF</Button>
          {s.talk_id && <Button variant="outline" className="border-slate-700 bg-transparent" onClick={() => downloadTalk(s.talk_id, lang)} data-testid="training-handout-btn"><Download className="mr-1 h-4 w-4" />{lang} handout</Button>}
          {open && can("training", "check") && meta && <Button className="bg-rose-600 hover:bg-rose-700" onClick={() => setSigning(true)} data-testid="add-attendee-btn"><PenLine className="mr-1 h-4 w-4" />Add attendee & sign</Button>}
          {open && can("training", "check") && <Button variant="ghost" onClick={() => act(() => api.post(`/training/${id}/close`), "Session closed")} data-testid="close-training-btn"><Lock className="mr-1 h-4 w-4" />Close session</Button>}
        </div>
      </div>
      <Info s={s} lang={lang} setLang={setLang} />
      <div className="panel overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-slate-800 text-left"><tr>{["Name", "Language", "Understood", ...(s.quiz.length ? ["Quiz"] : []), "Signed", "Signature", ""].map((h, i) => <th key={i} className="eyebrow px-3 py-3 font-normal">{h}</th>)}</tr></thead>
          <tbody>
            {s.attendees.map((a) => (
              <tr key={a.id} className="border-b border-slate-800/70" data-testid={`attendee-${a.name}`}>
                <td className="px-3 py-2 font-semibold">{a.name}</td><td className="px-3 py-2">{a.language}</td><td className="px-3 py-2 text-emerald-400">Yes</td>
                {s.quiz.length > 0 && <td className="px-3 py-2"><RagBadge status={a.passed ? "green" : "red"} label={`${a.score}% ${a.passed ? "pass" : "retrain"}`} /></td>}
                <td className="whitespace-nowrap px-3 py-2 text-xs">{fmtDateTime(a.signed_at)}</td>
                <td className="px-3 py-2"><img src={a.signature} alt="signature" className="h-10 rounded bg-white" /></td>
                <td className="px-3 py-2">{can("training", "manage") && open && <button onClick={() => window.confirm(`Remove ${a.name}?`) && act(() => api.delete(`/training/${id}/attendees/${a.id}`), "Attendee removed")} className="text-red-300" data-testid={`remove-attendee-${a.name}`}><Trash2 className="h-4 w-4" /></button>}</td>
              </tr>
            ))}
            {!s.attendees.length && <tr><td colSpan={7} className="px-3 py-10 text-center text-slate-500">No attendees signed yet.</td></tr>}
          </tbody>
        </table>
      </div>
      {meta && <SignDialog s={s} meta={meta} open={signing} onClose={() => setSigning(false)} onSigned={load} />}
    </div>
  );
}
